/**
 * HOOK: usePacmanMovement
 * Gestisce movimento e animazione di Pacman
 */

import { useState, useCallback } from "react";
import { PacmanState, Direction } from "../types/GameTypes";
import { GAME_CONFIG, PACMAN_SPAWN } from "../types/GameConstants";

export function usePacmanMovement(
    isValidPosition: (x: number, y: number) => boolean,
    width: number,
) {
    const [pacman, setPacman] = useState<PacmanState>({
        x: PACMAN_SPAWN.x,
        y: PACMAN_SPAWN.y,
        direction: "right",
        nextDirection: null,
    });
    const [pacmanMouth, setPacmanMouth] = useState(0);

    const getNewPosition = useCallback(
        (x: number, y: number, direction: Direction, speed: number) => {
            switch (direction) {
                case "up":
                    return { x, y: y - speed };
                case "down":
                    return { x, y: y + speed };
                case "left":
                    return { x: x - speed, y };
                case "right":
                    return { x: x + speed, y };
            }
        },
        [],
    );

    const handleInput = useCallback(
        (direction: Direction) => {
            setPacman((prev) => {
                // Check if can turn immediately
                const testPos = getNewPosition(
                    prev.x,
                    prev.y,
                    direction,
                    GAME_CONFIG.PACMAN_SPEED,
                );
                if (
                    isValidPosition(testPos.x, testPos.y) &&
                    prev.direction !== direction
                ) {
                    return { ...prev, direction, nextDirection: null };
                } else {
                    return { ...prev, nextDirection: direction };
                }
            });
        },
        [isValidPosition, getNewPosition],
    );

    const updatePacman = useCallback(
        (_deltaTime: number) => {
            setPacman((prev) => {
                let newX = prev.x;
                let newY = prev.y;
                let currentDirection = prev.direction;

                // Try to turn if there's a pending direction
                if (prev.nextDirection) {
                    const testPos = getNewPosition(
                        prev.x,
                        prev.y,
                        prev.nextDirection,
                        GAME_CONFIG.PACMAN_SPEED,
                    );
                    if (isValidPosition(testPos.x, testPos.y)) {
                        currentDirection = prev.nextDirection;
                    }
                }

                // Move in current direction
                const nextPos = getNewPosition(
                    prev.x,
                    prev.y,
                    currentDirection,
                    GAME_CONFIG.PACMAN_SPEED,
                );

                if (isValidPosition(nextPos.x, nextPos.y)) {
                    newX = nextPos.x;
                    newY = nextPos.y;
                }

                // Tunnel wraparound
                if (newX < 0) newX = width - GAME_CONFIG.CELL_SIZE;
                if (newX >= width) newX = GAME_CONFIG.CELL_SIZE;

                return {
                    x: newX,
                    y: newY,
                    direction: currentDirection,
                    nextDirection:
                        prev.nextDirection === currentDirection
                            ? null
                            : prev.nextDirection,
                };
            });

            // Animate Pac-Man mouth
            setPacmanMouth((prev) => (prev + 0.2) % (Math.PI * 2));
        },
        [isValidPosition, getNewPosition, width],
    );

    const resetPacman = useCallback(() => {
        setPacman({
            x: PACMAN_SPAWN.x,
            y: PACMAN_SPAWN.y,
            direction: "right",
            nextDirection: null,
        });
    }, []);

    return {
        pacman,
        pacmanMouth,
        handleInput,
        updatePacman,
        resetPacman,
    };
}
